import React, { useState } from 'react';

interface PhoneInputProps {
  label: string;
  phones: string[];
  onPhonesChange: (phones: string[]) => void;
  disabled?: boolean;
}

const COUNTRY_CODES = [
  { code: '+1', country: 'US/CA' },
  { code: '+44', country: 'UK' },
  { code: '+91', country: 'IN' },
  { code: '+86', country: 'CN' },
  { code: '+81', country: 'JP' },
  { code: '+49', country: 'DE' },
  { code: '+33', country: 'FR' },
  { code: '+39', country: 'IT' },
  { code: '+34', country: 'ES' },
  { code: '+61', country: 'AU' },
  { code: '+55', country: 'BR' },
  { code: '+52', country: 'MX' },
  { code: '+82', country: 'KR' },
  { code: '+31', country: 'NL' },
  { code: '+46', country: 'SE' },
  { code: '+47', country: 'NO' },
  { code: '+48', country: 'PL' },
  { code: '+41', country: 'CH' },
  { code: '+43', country: 'AT' },
  { code: '+32', country: 'BE' },
  { code: '+45', country: 'DK' },
  { code: '+358', country: 'FI' },
  { code: '+353', country: 'IE' },
  { code: '+351', country: 'PT' },
  { code: '+30', country: 'GR' },
  { code: '+420', country: 'CZ' },
  { code: '+36', country: 'HU' },
  { code: '+40', country: 'RO' },
  { code: '+7', country: 'RU' },
  { code: '+380', country: 'UA' },
  { code: '+27', country: 'ZA' },
  { code: '+234', country: 'NG' },
  { code: '+20', country: 'EG' },
  { code: '+212', country: 'MA' },
  { code: '+216', country: 'TN' },
  { code: '+254', country: 'KE' },
  { code: '+255', country: 'TZ' },
  { code: '+256', country: 'UG' },
  { code: '+233', country: 'GH' },
  { code: '+237', country: 'CM' },
  { code: '+225', country: 'CI' },
  { code: '+221', country: 'SN' },
  { code: '+60', country: 'MY' },
  { code: '+62', country: 'ID' },
  { code: '+63', country: 'PH' },
  { code: '+65', country: 'SG' },
  { code: '+66', country: 'TH' },
  { code: '+84', country: 'VN' },
  { code: '+880', country: 'BD' },
  { code: '+92', country: 'PK' },
  { code: '+93', country: 'AF' },
  { code: '+94', country: 'LK' },
  { code: '+95', country: 'MM' },
  { code: '+977', country: 'NP' },
  { code: '+90', country: 'TR' },
  { code: '+98', country: 'IR' },
  { code: '+964', country: 'IQ' },
  { code: '+966', country: 'SA' },
  { code: '+971', country: 'AE' },
  { code: '+972', country: 'IL' },
  { code: '+962', country: 'JO' },
  { code: '+961', country: 'LB' },
  { code: '+963', country: 'SY' },
  { code: '+970', country: 'PS' },
  { code: '+968', country: 'OM' },
  { code: '+967', country: 'YE' },
  { code: '+965', country: 'KW' },
  { code: '+973', country: 'BH' },
  { code: '+974', country: 'QA' },
  { code: '+213', country: 'DZ' },
  { code: '+218', country: 'LY' },
  { code: '+249', country: 'SD' },
  { code: '+251', country: 'ET' },
  { code: '+252', country: 'SO' },
  { code: '+253', country: 'DJ' },
  { code: '+258', country: 'MZ' },
  { code: '+260', country: 'ZM' },
  { code: '+263', country: 'ZW' },
  { code: '+264', country: 'NA' },
  { code: '+265', country: 'MW' },
  { code: '+266', country: 'LS' },
  { code: '+267', country: 'BW' },
  { code: '+268', country: 'SZ' },
  { code: '+269', country: 'KM' },
  { code: '+54', country: 'AR' },
  { code: '+56', country: 'CL' },
  { code: '+57', country: 'CO' },
  { code: '+58', country: 'VE' },
  { code: '+51', country: 'PE' },
  { code: '+593', country: 'EC' },
  { code: '+595', country: 'PY' },
  { code: '+598', country: 'UY' },
  { code: '+591', country: 'BO' },
  { code: '+592', country: 'GY' },
  { code: '+594', country: 'GF' },
  { code: '+597', country: 'SR' },
  { code: '+500', country: 'FK' },
].sort((a, b) => a.code.localeCompare(b.code));

export const PhoneInput: React.FC<PhoneInputProps> = ({
  label,
  phones,
  onPhonesChange,
  disabled = false
}) => {
  const [countryCode, setCountryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');

  const handleAddPhone = () => {
    const fullPhone = `${countryCode} ${phoneNumber.trim()}`;
    if (phoneNumber.trim() && !phones.includes(fullPhone)) {
      onPhonesChange([...phones, fullPhone]);
      setPhoneNumber('');
    }
  };

  const handleRemovePhone = (index: number) => {
    onPhonesChange(phones.filter((_, i) => i !== index));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9\s-]/g, '');
    setPhoneNumber(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddPhone();
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <div className="space-y-2">
        {phones.map((phone, index) => (
          <div key={index} className="flex items-center space-x-2">
            <span className="flex-1 px-3 py-2 bg-gray-100 rounded-md text-sm">
              {phone}
            </span>
            {!disabled && (
              <button
                type="button"
                onClick={() => handleRemovePhone(index)}
                className="text-red-600 hover:text-red-800"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        ))}
        {!disabled && (
          <div className="flex space-x-2">
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              {COUNTRY_CODES.map(({ code, country }) => (
                <option key={code} value={code}>
                  {code} ({country})
                </option>
              ))}
            </select>
            <input
              type="tel"
              value={phoneNumber}
              onChange={handlePhoneChange}
              onKeyPress={handleKeyPress}
              placeholder="Phone number"
              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
            <button
              type="button"
              onClick={handleAddPhone}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Add
            </button>
          </div>
        )}
      </div>
    </div>
  );
};